/* eslint-disable react-hooks/exhaustive-deps */
import { Dispatch, ReactNode, SetStateAction, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { APP_CONFIG, SOCKET_CONNECTION_TYPE } from "../../constant/common";
import { SOCKET_URL, API_KEY } from "../../constant/environment";
import { setStreamingActiveMirror } from "../../utils/streamingState";
import { ensureChatId } from "../../utils/chat/chatId";
import { continuesMainChat, getChatTitle } from "../../utils/chat/chatTitle";
import { captureException } from "@sentry/react";
import { v2orch } from "../../utils/v2OrchLog";
import { encodeBody, PayloadTooLargeError, decodeIncomingData } from "../../utils/wsPayloadCodec";
import { toastMessage } from "../../utils/ToastProvider";
import { resolveBoolFlag } from "../../utils/appConfig/resolveBoolFlag";
import { StateContext } from "../globalContext/context";
import { iGlobalContext } from "../../context/globalContext/interface";
import { sanitizeBodyForLLM } from "../../utils/messagesSanitizer";
import { flushSync } from "react-dom";
import { openSseChatStream } from "../../utils/sse/sseChatClient";
import { consumeSseStallMs } from "../../utils/sse/sseFault";
import { isSseTransportEnabled, isXeniaPortedRequestType, XENIA_CHAT_URL } from "../../utils/sse/sseTransport";
import { encryptData } from "../../utils/api/encryption";
import { getActingPartner } from "../../utils/actingPartner";
import { voiceBus } from "../../services/voice/voiceBus";

export interface iSocketContext {
  socketUrl: any;
  setSocketUrl: any;
  sendMessage: any;
  lastMessage: any;
  readyState: any;
  connectionStatus: any;
  streamingFlagAndResponseType: iStreamingFlagAndResponseType;
  setStreamingFlagAndResponseType: Dispatch<SetStateAction<iStreamingFlagAndResponseType>>;
  handleCloseSocketConnection: any;
  handleOpenSocketConnection: any;
  turnTokenRef: { current: number };
  lastChatPayloadRef: { current: any };
}
export interface iStreamingFlagAndResponseType {
  isStillStreamingFlag: boolean;
  responseType: string;
  chatId: string;
  showMoreClickFlag: boolean;
  booking?: boolean;
  socketOpenFrom?: string;
}

const readyStateToStatus = (rs: ReadyState) =>
  ({
    [ReadyState.CONNECTING]: SOCKET_CONNECTION_TYPE.connecting,
    [ReadyState.OPEN]: SOCKET_CONNECTION_TYPE.open,
    [ReadyState.CLOSING]: SOCKET_CONNECTION_TYPE.closing,
    [ReadyState.CLOSED]: SOCKET_CONNECTION_TYPE.closed,
    [ReadyState.UNINSTANTIATED]: SOCKET_CONNECTION_TYPE.unInstantiated,
  })[rs];

const SocketContext = createContext({} as iSocketContext);
export const SocketContextProvider = ({ children }: { children: ReactNode }) => {
  // SocketContextProvider is nested inside StateProvider (see context/index.tsx),
  // so reading appConfig directly is safe. Two backend-controlled flags drive
  // outbound WS payload encoding — they're independent and stack:
  //
  //   ENABLE_COMPRESSION    → gzip + base64 wrap on the wire (byte savings).
  //   ENABLE_TOON_ENCODING  → re-encode hotel/flight blobs inside `messages[i].content`
  //                            as TOON instead of JSON (LLM token savings).
  //
  // Both can be flipped per environment via the same appConfig pipeline that
  // drives USE_HOTEL_SEARCH_V2 / ENABLE_ACTIVITIES / etc. — no redeploy needed.
  const { appConfig, accessJWT, chatData }: iGlobalContext = useContext(StateContext);
  const compressionEnabled = resolveBoolFlag(appConfig, APP_CONFIG.ENABLE_COMPRESSION);
  const toonEnabled = resolveBoolFlag(appConfig, APP_CONFIG.ENABLE_TOON_ENCODING);
  const [socketUrl, setSocketUrl] = useState<any>("");
  const [streamingFlagAndResponseType, setStreamingFlagAndResponseType] = useState<iStreamingFlagAndResponseType>({
    isStillStreamingFlag: false,
    responseType: "",
    chatId: "",
    showMoreClickFlag: false,
  });
  useEffect(() => {
    setStreamingActiveMirror(streamingFlagAndResponseType?.isStillStreamingFlag === true);
  }, [streamingFlagAndResponseType?.isStillStreamingFlag]);
  const { sendMessage, lastMessage: rawLastMessage, readyState } = useWebSocket(socketUrl, {
    // shouldReconnect: () => false, // Prevent automatic reconnection when closed
    // protocols: "x-access-token",
    shouldReconnect: () => true, // Reconnect only if not manually closed
    reconnectAttempts: 2, // Max retries
    reconnectInterval: 3000, // Wait 3 seconds before retrying
  });
  // Inflate gzip-b64 frames ONCE here at the transport layer so ALL lastMessage
  // consumers (DataStreamingContext, useItineraryDetailsHook, askZoya, Lares,
  // todayLanding, ExploreTripAndAssistance) receive the decoded frame — the backend
  // compresses large multi-city hotel/flight results to fit API Gateway's 128 KB
  // frame cap (see wsPayloadCodec.decodeIncomingData). Plain (non-envelope) frames
  // pass through by reference (identity preserved → the `[lastMessage]` effect deps
  // are unaffected); gzip keeps a big result in ONE frame, so no burst / frame-drop
  // risk from useWebSocket's single-slot lastMessage.
  const lastMessage = useMemo(() => {
    const data = (rawLastMessage as any)?.data;
    if (typeof data !== "string") return rawLastMessage;
    const decoded = decodeIncomingData(data);
    return decoded === data ? rawLastMessage : ({ data: decoded } as any);
  }, [rawLastMessage]);
  const connectionStatus = readyStateToStatus(readyState);

  const sseEnabled = isSseTransportEnabled(appConfig);
  const [sseLastMessage, setSseLastMessage] = useState<any>(null);
  const [sseReadyState, setSseReadyState] = useState<ReadyState>(ReadyState.UNINSTANTIATED);
  const sseAbortRef = useRef<AbortController | null>(null);
  const sseStreamIdRef = useRef(0);
  const turnTokenRef = useRef(0);
  const lastChatPayloadRef = useRef<any>(null);
  const sseStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeTransport, setActiveTransport] = useState<"ws" | "sse">(sseEnabled ? "sse" : "ws");
  useEffect(() => {
    setActiveTransport(sseEnabled ? "sse" : "ws");
  }, [sseEnabled]);
  const effectiveLastMessage = activeTransport === "sse" ? sseLastMessage : lastMessage;
  const effectiveReadyState = activeTransport === "sse" ? sseReadyState : readyState;
  const effectiveConnectionStatus =
    activeTransport === "sse" ? readyStateToStatus(sseReadyState) : connectionStatus;

  const [manualClose, setManualClose] = useState(false);
  // Holds the latest message that was queued before the socket finished
  // opening. useWebSocket only flushes sendMessage when readyState === OPEN;
  // calling sendMessage during CONNECTING silently drops the message.
  const pendingMessageRef = useRef<string | null>(null);
  // Tracks the previous connectionStatus so we only emit ws.open/ws.close
  // logs on actual transitions, not on every render.
  const prevConnectionStatusRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const prev = prevConnectionStatusRef.current;
    prevConnectionStatusRef.current = connectionStatus;
    if (prev === connectionStatus) return;
    if (connectionStatus === SOCKET_CONNECTION_TYPE.open) {
      v2orch("ws.open", { url: typeof socketUrl === "string" ? socketUrl : SOCKET_URL });
      // Flush any message that was queued while the socket was connecting.
      if (pendingMessageRef.current) {
        try {
          sendMessage(pendingMessageRef.current);
        } catch (e: any) {
          v2orch("ws.flush-pending-failed", { message: e?.message });
        }
        pendingMessageRef.current = null;
      }
    } else if (connectionStatus === SOCKET_CONNECTION_TYPE.closed) {
      v2orch("ws.close", { manualClose });
    } else if (connectionStatus === SOCKET_CONNECTION_TYPE.closing) {
      v2orch("ws.closing", {});
    } else if (connectionStatus === SOCKET_CONNECTION_TYPE.connecting) {
      v2orch("ws.connecting", { url: typeof socketUrl === "string" ? socketUrl : SOCKET_URL });
    }
  }, [connectionStatus, sendMessage, socketUrl, manualClose]);

  function openSseTurn(message: any) {
    setManualClose(false);
    const wireMessages = Array.isArray(message?.messages) ? message.messages : null;
    const lastWireMessage = wireMessages?.[wireMessages.length - 1];
    const wireBody =
      wireMessages &&
      wireMessages.length > 0 &&
      message?.userInput &&
      lastWireMessage?.role === "user" &&
      lastWireMessage?.content === message.userInput
        ? { ...message, messages: wireMessages.slice(0, -1) }
        : message;
    const slimMessage = sanitizeBodyForLLM(wireBody, { useToon: false });
    const traceId = message?.traceId || Math.random().toString(36).slice(2, 10);
    const turn = {
      requestType: message?.requestType ?? "",
      voice: message?.voice === true,
      traceId,
      sent: { userInput: message?.userInput, voiceReply: message?.voiceReply },
    };
    sseAbortRef.current?.abort();
    const controller = new AbortController();
    sseAbortRef.current = controller;
    const streamId = ++sseStreamIdRef.current;
    const isCurrent = () => streamId === sseStreamIdRef.current;
    let handled = false;
    let firstFrame = true;
    const stallOverrideMs = consumeSseStallMs();
    const CONNECT_STALL_MS = stallOverrideMs ?? 60000;
    const STEADY_STALL_MS = stallOverrideMs ?? 45000;
    const clearStall = () => {
      if (sseStallTimerRef.current) {
        clearTimeout(sseStallTimerRef.current);
        sseStallTimerRef.current = null;
      }
    };
    const armStall = (ms: number) => {
      clearStall();
      sseStallTimerRef.current = setTimeout(() => {
        if (!isCurrent()) return;
        v2orch("sse.stall", { traceId, ms });
        handled = true;
        controller.abort();
        setSseReadyState(ReadyState.CLOSED);
        flushSync(() =>
          setSseLastMessage({
            data: JSON.stringify({
              type: "error",
              code: 504,
              message: "stream stalled",
              error: "stream stalled",
            }),
            turn,
          }),
        );
      }, ms);
    };
    setSseLastMessage(null);
    setSseReadyState((rs) => (rs === ReadyState.OPEN ? rs : ReadyState.CONNECTING));
    v2orch("sse.send", { traceId, requestType: message?.requestType ?? "itinerary" });
    armStall(CONNECT_STALL_MS);
    void openSseChatStream(
      XENIA_CHAT_URL,
      encryptData(slimMessage),
      {
        "Content-Type": "text/plain",
        "x-access-token": accessJWT,
        "x-api-key": API_KEY,
        "x-trace-id": traceId,
      },
      controller.signal,
      {
        onOpen: (tid) => {
          if (!isCurrent()) return;
          setSseReadyState(ReadyState.OPEN);
          v2orch("sse.open", { traceId: tid || traceId });
          armStall(STEADY_STALL_MS);
          flushSync(() => setSseLastMessage({ data: JSON.stringify({ type: "status", status: "connected" }), turn }));
        },
        onFrame: (data) => {
          if (!isCurrent()) return;
          try {
            const frame = JSON.parse(data);
            if (firstFrame && frame?.type !== "heartbeat") {
              firstFrame = false;
              if (frame?.type !== "masked") v2orch("frame.masked.none", { traceId, requestType: turn.requestType });
            }
            if (
              frame?.type === "done" ||
              frame?.type === "error" ||
              (frame?.type === "status" && frame?.status === "completed")
            ) {
              handled = true;
            }
          } catch {}
          flushSync(() => setSseLastMessage({ data, turn }));
        },
        onActivity: () => {
          if (isCurrent()) armStall(STEADY_STALL_MS);
        },
        onBuffered: () => {
          if (!isCurrent()) return;
          clearStall();
          handled = true;
          v2orch("sse.buffered", { traceId });
          setSseLastMessage({ data: JSON.stringify({ type: "error", error: "streaming disabled upstream" }), turn });
        },
        onError: (err) => {
          if (!isCurrent()) return;
          clearStall();
          handled = true;
          v2orch("sse.error", { traceId, message: err?.message });
          setSseLastMessage({ data: JSON.stringify({ type: "error", error: err?.message ?? "stream error" }), turn });
        },
        onClose: () => {
          if (!isCurrent()) return;
          clearStall();
          v2orch("sse.close", { traceId, handled });
          if (!handled) {
            flushSync(() =>
              setSseLastMessage({
                data: JSON.stringify({
                  type: "error",
                  code: 503,
                  message: "connection closed",
                  error: "connection closed mid-stream",
                }),
                turn,
              }),
            );
          }
          voiceBus.requestClosed();
        },
      },
    );
  }

  function withActingPartner(message: any) {
    if (!message || typeof message !== "object") return message;
    const acting = getActingPartner();
    if (!acting?.partnerCode) return message;
    return {
      ...message,
      actingPartner: {
        partnerCode: acting.partnerCode,
        ...(acting.branchCode ? { branchCode: acting.branchCode } : {}),
        ...(acting.channel ? { channel: acting.channel } : {}),
      },
    };
  }

  function handleOpenSocketConnection(rawMessage: any) {
    const message = withActingPartner(rawMessage);
    turnTokenRef.current += 1;
    if (message && typeof message === "object" && !message.chatId) {
      message.chatId = ensureChatId();
    }
    const useSse = sseEnabled && isXeniaPortedRequestType(message?.requestType);
    const chatTitle = getChatTitle(chatData);
    if (useSse && chatTitle && continuesMainChat(message) && !message.chatTitle) {
      message.chatTitle = chatTitle;
    }
    const isLlmTurn =
      message &&
      typeof message === "object" &&
      message.requestType !== "voiceExchange" &&
      (message.messages !== undefined ||
        message.userInput !== undefined ||
        message.iteneryInput !== undefined);
    if (isLlmTurn) {
      try {
        lastChatPayloadRef.current = JSON.parse(JSON.stringify(message));
      } catch {
        lastChatPayloadRef.current = message;
      }
    }
    v2orch("ws.chatId", { chatId: message?.chatId, requestType: message?.requestType, chatTitle: message?.chatTitle });
    setActiveTransport(useSse ? "sse" : "ws");
    if (useSse) {
      voiceBus.requestOpened(message);
      openSseTurn(message);
      return;
    }
    setManualClose(false);
    // Slim the `messages` array first — the backend stamps raw hotel/flight
    // API responses into prompt history (cloudService.js push points), and
    // the frontend relays the full prompt back every turn. Subtractive deep
    // strip of universally-useless fields (image URLs, encrypted IDs, scoring
    // engine internals, supplier monitoring) — keeps everything a user could
    // plausibly ask about (amenities, descriptions, equipment, terminals,
    // layover times, fare basis, etc.). Pure passthrough when no `messages`
    // field is present, so non-LLM senders (status pings, voice channel)
    // are unaffected.
    //
    // When `toonEnabled`, recognised hotel/flight blobs in assistant content
    // are additionally re-encoded as TOON for the ~40-45% LLM token win on
    // top of the size reduction. Both passes run independently of the
    // compression flag since they're valuable on their own.
    const slimMessage = sanitizeBodyForLLM(message, { useToon: toonEnabled });

    // Encode the slimmed body through the compression codec. Gated on the
    // backend ENABLE_COMPRESSION appConfig flag — when off, bypass entirely
    // and behave exactly as before. Keeps rollout staged: backend ships
    // decompression support, then flips the config row.
    //
    // Codec returns either a passthrough frame (small body, no envelope) or a
    // gzip-b64 envelope. Throws PayloadTooLargeError if compressed bytes
    // exceed the WS ceiling — we surface a toast and skip the send rather
    // than letting API Gateway silently drop the connection.
    let serialized: string;
    if (compressionEnabled) {
      let encoded;
      try {
        encoded = encodeBody(slimMessage);
      } catch (e: any) {
        if (e instanceof PayloadTooLargeError) {
          v2orch("ws.encode-too-large", {
            rawBytes: e.rawBytes,
            compressedBytes: e.compressedBytes,
          });
          toastMessage.error(
            "This conversation has grown too long for one request. Please start a new chat to continue.",
          );
          return;
        }
        v2orch("ws.encode-failed", { message: e?.message });
        captureException(e);
        return;
      }
      v2orch("ws.encode", {
        rawBytes: encoded.rawBytes,
        compressedBytes: encoded.compressedBytes,
        passthrough: encoded.passthrough,
      });
      serialized = JSON.stringify(encoded.frame);
    } else {
      serialized = JSON.stringify(slimMessage);
    }
    voiceBus.requestOpened(message);

    // Cycle socketUrl so useWebSocket reopens a fresh connection if the prior
    // one was closed by handleCloseSocketConnection.
    if (readyState === ReadyState.OPEN) {
      try {
        sendMessage(serialized);
      } catch (e: any) {
        v2orch("ws.send-failed", { message: e?.message });
        captureException(e);
      }
      return;
    }
    pendingMessageRef.current = serialized;
    setSocketUrl((prev: any) => null);
    setSocketUrl((prev: any) => SOCKET_URL);
  }
  function handleCloseSocketConnection() {
    voiceBus.requestClosed();
    setManualClose(true);
    sseAbortRef.current?.abort();
    sseStreamIdRef.current += 1;
    if (sseStallTimerRef.current) {
      clearTimeout(sseStallTimerRef.current);
      sseStallTimerRef.current = null;
    }
    setSseReadyState(ReadyState.CLOSED);
    setSocketUrl(null);
    pendingMessageRef.current = null;
    setStreamingFlagAndResponseType({
      chatId: "",
      isStillStreamingFlag: false,
      responseType: "",
      showMoreClickFlag: false,
      socketOpenFrom: "",
    });
  }

  const value = useMemo(
    () => ({
      handleOpenSocketConnection,
      handleCloseSocketConnection,
      socketUrl,
      setSocketUrl,
      sendMessage,
      lastMessage: effectiveLastMessage,
      readyState: effectiveReadyState,
      connectionStatus: effectiveConnectionStatus,
      streamingFlagAndResponseType,
      setStreamingFlagAndResponseType,
      turnTokenRef,
      lastChatPayloadRef,
    }),
    [
      handleOpenSocketConnection,
      handleCloseSocketConnection,
      socketUrl,
      setSocketUrl,
      sendMessage,
      effectiveLastMessage,
      effectiveReadyState,
      effectiveConnectionStatus,
      streamingFlagAndResponseType,
      setStreamingFlagAndResponseType,
    ]
  );
  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};
export const useSocketContext = () => useContext(SocketContext);
